/**
 * auth.ts — child + parent auth endpoints (PR-15 split of api/client.ts).
 *
 * Holds the register / login / password-reset / logout calls for BOTH
 * account types. Parent calls live here even though the kid app is the
 * primary consumer because the wizard `LoginPage` flips between modes on
 * the same screen — keeping both flows in one file mirrors how the UI
 * presents them.
 *
 * 调用关系：
 *   - Merged into the unified `api` object exported from api/client.ts,
 *     so call sites still write `api.login(...)`, `api.parentRegisterStart(...)`.
 *   - Used directly by pages/LoginPage.tsx, pages/MeSettingsPage.tsx
 *     (for logout), and the parent admin via the shared session keys.
 *   - Backend endpoints: backend/src/routes/auth.js
 *     (`/api/v1/auth/child/*` and `/api/v1/auth/parent/*`).
 *
 * 关键约束：
 *   - On success the caller must invoke setSession() from _core.ts to
 *     persist the bearer token in localStorage; these functions only
 *     POST and return the typed response.
 */
import type { AuthSessionRes } from '@xiaopeiban/shared';
import { call } from './_core';

export const authApi = {
  registerStart(name: string, password: string, parent_email?: string) {
    return call<{ ok: true; message?: string; devCode?: string; standalone?: boolean } & Partial<AuthSessionRes>>(
      '/api/v1/auth/child/register-start',
      {
        method: 'POST',
        body: JSON.stringify({
          name,
          password,
          ...(parent_email ? { parent_email } : {}),
        }),
      },
    );
  },
  registerComplete(name: string, parent_email: string, code: string) {
    return call<AuthSessionRes>('/api/v1/auth/child/register-complete', {
      method: 'POST',
      body: JSON.stringify({ name, parent_email, code }),
    });
  },
  login(name: string, password: string) {
    return call<AuthSessionRes>('/api/v1/auth/child/login', {
      method: 'POST',
      body: JSON.stringify({ name, password }),
    });
  },
  // ─── Parent auth (login + register on the same /login page) ─────────────
  // The parent admin lives at /parent/* on this same domain. Both apps share
  // localStorage keys, so a parent token written here is visible there.
  parentLoginWithPassword(email: string, password: string) {
    return call<AuthSessionRes>('/api/v1/auth/parent/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  parentRequestLoginCode(email: string) {
    return call<{ ok: true; devCode?: string }>(
      '/api/v1/auth/parent/request-login-code',
      { method: 'POST', body: JSON.stringify({ email }) },
    );
  },
  parentVerifyLoginCode(email: string, code: string) {
    return call<AuthSessionRes>('/api/v1/auth/parent/verify-login-code', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  },
  parentRegisterStart(email: string, password: string, inviteCode?: string) {
    return call<{ ok: true; message: string; devCode?: string }>(
      '/api/v1/auth/parent/register-start',
      {
        method: 'POST',
        body: JSON.stringify({
          email, password,
          ...(inviteCode ? { invite_code: inviteCode } : {}),
        }),
      },
    );
  },
  parentRegisterComplete(email: string, code: string) {
    return call<AuthSessionRes>('/api/v1/auth/parent/register-complete', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  },
  parentPasswordResetStart(email: string) {
    return call<{ ok: true; message: string; devCode?: string }>(
      '/api/v1/auth/parent/password-reset-start',
      { method: 'POST', body: JSON.stringify({ email }) },
    );
  },
  parentPasswordResetComplete(email: string, code: string, password: string) {
    return call<{ ok: true }>('/api/v1/auth/parent/password-reset-complete', {
      method: 'POST',
      body: JSON.stringify({ email, code, password }),
    });
  },
  logout() {
    return call<{ ok: true }>('/api/v1/auth/logout', { method: 'POST' });
  },
};
